/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { CosmosIconLinkGroupFragment } from "@cosmos/hygraph-schema";

import { CosmosLink, CosmosRichText, EdsIcon } from "@components/elements";
import { css } from "@panda/css";
import { HStack } from "@panda/jsx";
import React from "react";

const iconLinkStyle = css({
	color: "eds.text.secondary",
	transition: `color token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)`,
	_hover: {
		color: "eds.text.primary",
	},
});

export const IconLinkGroup = ({ description, edsIconLinks }: CosmosIconLinkGroupFragment): React.JSX.Element | null => {
	if (!edsIconLinks?.length) return null;

	return (
		<HStack gap="eds.dimension.24" justifyContent="center">
			<CosmosRichText variant="eyebrowMD" html={description} />
			<HStack gap="eds.dimension.4">
				{edsIconLinks.map((link, i) => {
					if (!link.icon) return null;

					const icon = <EdsIcon key={`icon-${i}`} icon={link.icon} />;

					if (link.href) {
						return (
							<CosmosLink
								key={`icon-link-${i}`}
								href={link.href}
								aria-label={link.ariaLabel || undefined}
								className={iconLinkStyle}
								analytics={{
									eventCategory: "hyperlink",
									eventLabel: link.ariaLabel || "",
									eventValue: {
										component: "IconLinkGroup",
										href: link.href,
									},
								}}
							>
								{icon}
							</CosmosLink>
						);
					}

					return icon;
				})}
			</HStack>
		</HStack>
	);
};
